import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '../../store'
import { Task } from './types'

export const selectFetchAssignedTask = (state: RootState) => state.manageTasks.assignedTask
export const selectTasks = (state: RootState) => state.manageTasks.tasks
export const selectSelectedTask = (state: RootState) => state.manageTasks.selectedTask
export const selectTasksLoading = (state: RootState) => state.manageTasks.loading
export const selectUpdateLoading = (state: RootState) => state.manageTasks.updateLoading
export const selectSelectedTaskLoading = (state: RootState) => state.manageTasks.selectedTaskLoading
export const selectTasksError = (state: RootState) => state.manageTasks.error
export const selectSelectedTaskError = (state: RootState) => state.manageTasks.selectedTaskError
export const selectFilters = (state: RootState) => state.manageTasks.filters
export const selectSearchQuery = (state: RootState) => state.manageTasks.searchQuery
export const selectTeamMembers = (state: RootState) => state.teamMember.teamMembers
export const selectListOfTasksReport = (state: RootState) => state.taskDetailsReport.listOfTasks
export const selectDateRange = (state: RootState) => state.manageTasks.dateRange

export const selectLoadingUpdateAssignedTeamMember = (state: RootState) =>
  state.manageTasks.loadingUpdateAssignedTeamMember
export const selectErrorUpdateAssignedTeamMember = (state: RootState) =>
  state.manageTasks.errorUpdateAssignedTeamMember

export const selectPendingTaskCount = createSelector([selectFetchAssignedTask], (task) => {
  return task.filter((task) => task.status !== 'completed' && task.isArchived === false).length
})

const sortByUpcomingStartDate = (tasks: Task[]) => {
  const now = new Date()
  return [...tasks].sort((a, b) => {
    if (a.dueDate === null && b.dueDate === null) return 0
    if (a.dueDate === null) return 1
    if (b.dueDate === null) return -1

    const dateA = new Date(a.dueDate)
    const dateB = new Date(b.dueDate)

    const today = new Date(now)
    today.setHours(0, 0, 0, 0)

    const isDateAToday = dateA.toDateString() === today.toDateString()
    const isDateBToday = dateB.toDateString() === today.toDateString()
    const isDateAFuture = dateA > today
    const isDateBFuture = dateB > today

    if (isDateAToday && isDateBToday) return dateA.getTime() - dateB.getTime()
    if (isDateAToday) return -1
    if (isDateBToday) return 1
    if (isDateAFuture && isDateBFuture) return dateA.getTime() - dateB.getTime()
    if (isDateAFuture) return -1
    if (isDateBFuture) return 1
    return dateB.getTime() - dateA.getTime()
  })
}

export const selectFilteredTasks = createSelector(
  [selectTasks, selectFilters, selectSearchQuery, selectTeamMembers, selectDateRange],
  (tasks, filters, searchQuery, teamMembers, dateRange) => {
    const hasOperationalManagerFilter =
      filters.operationalManager && filters.operationalManager.length > 0
    const hasSupervisorFilter = filters.supervisor && filters.supervisor.length > 0
    const hasFunctionalAreaFilter = filters.functionalArea && filters.functionalArea.length > 0

    // Build a lookup: supervisorName -> { operationalManagerName, functionalArea }
    // and functionalArea -> { operationalManagerName }
    // from the TeamMember roster, for hierarchy fallback
    const supervisorToOmAndFa: Record<string, { om: string; fa: string }> = {}
    const faToOm: Record<string, string> = {}
    for (const tm of teamMembers) {
      const supName = tm.supervisor?.name
      const omName = tm.operationalManager?.name
      const fa = tm.functionalArea
      if (supName && (omName || fa)) {
        supervisorToOmAndFa[supName] = {
          om: omName || supervisorToOmAndFa[supName]?.om || '',
          fa: fa || supervisorToOmAndFa[supName]?.fa || '',
        }
      }
      if (fa && omName) {
        faToOm[fa] = omName
      }
    }

    const filteredTasks = tasks.filter((task) => {
      // --- Operations Manager filter ---
      if (hasOperationalManagerFilter) {
        const selectedOMs = filters.operationalManager || []

        // Task's own OM names
        const taskOMNames = (task.operationalManager || []).map((om) => om.name).filter(Boolean)

        // Task's own supervisor names (for hierarchy: supervisor → OM)
        const taskSupervisorNames = (task.supervisor || []).map((sv) => sv.name).filter(Boolean)

        // Task's own functional areas (for hierarchy: FA → OM)
        const taskFAs = (task.functionalArea || []).filter(Boolean)

        // Resolve additional OM names via hierarchy
        const resolvedOMNames = new Set<string>(taskOMNames)
        for (const supName of taskSupervisorNames) {
          const resolved = supervisorToOmAndFa[supName]?.om
          if (resolved) resolvedOMNames.add(resolved)
        }
        for (const fa of taskFAs) {
          const resolved = faToOm[fa]
          if (resolved) resolvedOMNames.add(resolved)
        }

        const matchesOM = selectedOMs.some((om) => resolvedOMNames.has(om))
        if (!matchesOM) return false
      }

      // --- Supervisor filter ---
      if (hasSupervisorFilter) {
        const selectedSupervisors = filters.supervisor || []

        // Only match tasks that explicitly have the selected supervisor(s) assigned.
        // Do NOT expand via OM → supervisors hierarchy, as that would cause tasks
        // scoped to an OM (without a specific supervisor) to match every supervisor
        // under that OM, producing incorrect results.
        const taskSupervisorNames = (task.supervisor || []).map((sv) => sv.name).filter(Boolean)

        const matchesSupervisor = selectedSupervisors.some((sv) => taskSupervisorNames.includes(sv))
        if (!matchesSupervisor) return false
      }

      // --- Functional Area filter ---
      if (hasFunctionalAreaFilter) {
        const selectedFAs = filters.functionalArea || []

        // Task's own FAs
        const taskFAs = (task.functionalArea || []).filter(Boolean)

        // Task's supervisor names (for hierarchy: supervisor → FA)
        const taskSupervisorNames = (task.supervisor || []).map((sv) => sv.name).filter(Boolean)

        // Task's OM names (for reverse: OM → FAs)
        const taskOMNames = (task.operationalManager || []).map((om) => om.name).filter(Boolean)

        const resolvedFAs = new Set<string>(taskFAs)
        for (const supName of taskSupervisorNames) {
          const resolved = supervisorToOmAndFa[supName]?.fa
          if (resolved) resolvedFAs.add(resolved)
        }

        // If task has OM but no FA, look up FAs under that OM from team members
        if (taskFAs.length === 0 && taskSupervisorNames.length === 0) {
          for (const tm of teamMembers) {
            const tmOMName = tm.operationalManager?.name
            const tmFA = tm.functionalArea
            if (tmFA && taskOMNames.includes(tmOMName || '')) {
              resolvedFAs.add(tmFA)
            }
          }
        }

        const matchesFA = selectedFAs.some((fa) => resolvedFAs.has(fa))
        if (!matchesFA) return false
      }

      // --- Importance filter ---
      const importanceValues = filters.importance || []
      if (importanceValues.length > 0 && importanceValues.length < 2) {
        if (importanceValues.includes('Required') && task.importance !== 'Required') return false
        if (importanceValues.includes('Optional') && task.importance !== 'Optional') return false
      }

      // --- Search query ---
      const matchesSearch = searchQuery
        ? task.title.toLowerCase().includes(searchQuery.toLowerCase())
        : true
      if (!matchesSearch) return false

      // --- Created Date range filter ---
      if (dateRange.startDate || dateRange.endDate) {
        const taskCreatedAt = task.createdAt ? new Date(task.createdAt).getTime() : null
        if (taskCreatedAt !== null) {
          if (dateRange.startDate) {
            const start = new Date(dateRange.startDate)
            start.setHours(0, 0, 0, 0)
            if (taskCreatedAt < start.getTime()) return false
          }
          if (dateRange.endDate) {
            const end = new Date(dateRange.endDate)
            end.setHours(23, 59, 59, 999)
            if (taskCreatedAt > end.getTime()) return false
          }
        }
      }

      return true
    })

    return sortByUpcomingStartDate(filteredTasks)
  },
)

export const selectFilteredTasksIsNotArchived = createSelector([selectFilteredTasks], (filteredTasks) =>
  filteredTasks.filter((task) => !task.isArchived),
)

// Compliance metrics derived from the filtered (visible) task table rows,
// joined with the task report data which has completedOnTime and overdue counts.
// The report's listOfTasks uses taskId (numeric id as string) as the key,
// while manageTasks tasks have a numeric `id` field — join on those.
//
// When a supervisor filter is active, we use the per-supervisor breakdown
// (supervisorStats) from the report data to compute metrics only for team
// members under the selected supervisor(s) — not the full org-wide totals.
// This ensures the chart accurately reflects the filtered supervisor's team.
export const selectFilteredTaskComplianceMetrics = createSelector(
  [selectFilteredTasksIsNotArchived, selectListOfTasksReport, selectFilters],
  (filteredTasks, listOfTasks, filters) => {
    // Join key: reportTask._id = String(task.id) from manageTasks
    const reportMap = new Map(listOfTasks.map((t) => [t._id, t]))

    const selectedSupervisors = filters.supervisor && filters.supervisor.length > 0
      ? filters.supervisor
      : null

    let totalTasks = 0
    let totalCompleted = 0
    let totalCompletedOnTime = 0
    let totalOverdue = 0

    for (const task of filteredTasks) {
      const reportTask = reportMap.get(String(task.id))
      if (!reportTask) continue

      if (selectedSupervisors && reportTask.supervisorStats) {
        // Sum stats only for the selected supervisor(s) using the per-supervisor breakdown.
        // This prevents metrics from including team members under other supervisors
        // when a supervisor filter is active.
        for (const supName of selectedSupervisors) {
          const supStats = reportTask.supervisorStats[supName]
          if (!supStats) continue
          totalTasks += supStats.total
          totalCompleted += supStats.completed
          totalCompletedOnTime += supStats.completedOnTime
          totalOverdue += supStats.overdue
        }
      } else {
        // No supervisor filter (or supervisorStats not yet available) — use full task totals
        const taskTotal =
          reportTask.inProgressTeamMembers +
          reportTask.completedTeamMembers +
          reportTask.notStartedTeamMembers

        totalTasks += taskTotal
        totalCompleted += reportTask.completedTeamMembers
        totalCompletedOnTime += reportTask.completedOnTimeTeamMembers ?? 0
        totalOverdue += reportTask.overdueTeamMembers ?? 0
      }
    }

    return {
      overallComplianceRate:
        totalTasks > 0 ? parseFloat(((totalCompleted / totalTasks) * 100).toFixed(2)) : 0,
      onTimeDeliveryRate:
        totalCompleted > 0 ? parseFloat(((totalCompletedOnTime / totalCompleted) * 100).toFixed(2)) : 0,
      overdueRate: totalTasks > 0 ? parseFloat(((totalOverdue / totalTasks) * 100).toFixed(2)) : 0,
    }
  },
)
